import React, { useCallback, useMemo } from 'react';
import ReactFlow, { 
  addEdge, 
  Background, 
  Controls, 
  MiniMap, 
  Connection, 
  Edge, 
  Node,
  useNodesState,
  useEdgesState,
  NodeTypes
} from 'reactflow';
import 'reactflow/dist/style.css';
import { DunningStep } from '../types';
import DunningNode from './DunningNode';

const nodeTypes: NodeTypes = {
  dunningNode: DunningNode,
};

interface DunningFlowchartProps {
  steps: DunningStep[];
  onStepsChange: (steps: DunningStep[]) => void;
  onDeleteStep: (id: string) => void;
}

const DunningFlowchart: React.FC<DunningFlowchartProps> = ({ steps, onStepsChange, onDeleteStep }) => {
  const moveStep = (index: number, direction: number) => {
    const newSteps = [...steps];
    const targetIndex = index + direction;
    if (targetIndex >= 0 && targetIndex < newSteps.length) {
      [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
      onStepsChange(newSteps);
    }
  };

  const initialNodes: Node[] = useMemo(() => {
    return steps.map((step, index) => ({
      id: step.id,
      data: { 
        label: `${step.action} (Delay: ${step.dayDelay}d)`,
        onDelete: () => onDeleteStep(step.id),
        onMoveUp: () => moveStep(index, -1),
        onMoveDown: () => moveStep(index, 1),
      },
      position: { x: 100, y: index * 150 },
      type: 'dunningNode',
    }));
  }, [steps, onDeleteStep]);

  const initialEdges: Edge[] = useMemo(() => {
    return steps.slice(0, -1).map((step, index) => ({
      id: `e${step.id}-${steps[index + 1].id}`,
      source: step.id,
      target: steps[index + 1].id,
      animated: true,
    }));
  }, [steps]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback((params: Connection) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  return (
    <div style={{ height: '500px', width: '100%' }} className="bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-sm">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

export default DunningFlowchart;
